import { Typography } from '@remarkable/ark-web';

import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { SubscriptionMember } from 'src/api/endpoints/storeApi.types';
import { useUpdateMemberRole } from 'src/api/queries';
import { Button, Modal } from 'src/components';
import { formatRoleName } from 'src/utils/roleNameHelper';

export const RoleEnum = {
  OWNER: 'owner',
  MEMBER: 'member',
  USER_MANAGER: 'user_manager',
} as const;

type Role = (typeof RoleEnum)[keyof typeof RoleEnum];

const isRole = (role: string): role is Role => {
  return Object.values(RoleEnum).includes(role as Role);
};

const accessOptions = {
  editPaymentMethod: 'Edit payment method',
  viewBilingInfo: 'View billing information',
  seeReceipts: 'See receipts',
  inviteAndRemoveMembers: 'Invite and remove members',
  seeAllMembersAndPendingInvitations: 'See all members and pending invitations',
  assignOrRemoveOwnerAccess: 'Assign or remove owner access',
  cancelSubscription: 'Cancel your subscription',
} as const;

type AccessOption = (typeof accessOptions)[keyof typeof accessOptions];

const roleAccessOptions: Record<Role, AccessOption[]> = {
  [RoleEnum.OWNER]: [
    accessOptions.editPaymentMethod,
    accessOptions.viewBilingInfo,
    accessOptions.seeReceipts,
    accessOptions.inviteAndRemoveMembers,
    accessOptions.seeAllMembersAndPendingInvitations,
    accessOptions.assignOrRemoveOwnerAccess,
    accessOptions.cancelSubscription,
  ],
  [RoleEnum.USER_MANAGER]: [
    accessOptions.inviteAndRemoveMembers,
    accessOptions.seeAllMembersAndPendingInvitations,
  ],
  [RoleEnum.MEMBER]: [],
};

interface ChangeRoleProps {
  member: SubscriptionMember;
  toRole: string;
  onSuccess?: () => void;
}

export function UpdateMemberRole(props: ChangeRoleProps) {
  const updateMemberRole = useUpdateMemberRole();

  if (!isRole(props.toRole)) {
    // Fallback so that we can still have a functional component when adding new roles that
    // isn't fully defined yet
    return (
      <div className="flex flex-col gap-16">
        <Typography as="h3" variant="heading-md">
          Make {formatRoleName(props.toRole)}
        </Typography>

        <div className="flex flex-col flex-wrap justify-center gap-16 xs:flex-row">
          <Modal.Close asChild>
            <Button variant="secondary" className="w-full xs:w-fit">
              <span>Cancel</span>
            </Button>
          </Modal.Close>

          <Button
            loading={updateMemberRole.isPending}
            variant="primary"
            className="w-full xs:w-fit"
            onClick={() => {
              tracker.trackEvent(
                new ButtonClicked({
                  component_location:
                    ComponentLocations.MANAGE_CONNECT.MEMBERS_PAGE,
                  text: 'Assign access',
                  action: 'change member role to ' + props.toRole,
                })
              );

              updateMemberRole.mutate(
                {
                  memberId: props.member.id,
                  newRole: props.toRole,
                },
                { onSuccess: props.onSuccess }
              );
            }}
          >
            Assign access
          </Button>
        </div>
      </div>
    );
  }

  const accessOptions =
    props.member.role in roleAccessOptions
      ? roleAccessOptions[props.member.role]
      : [];

  if (props.toRole === RoleEnum.MEMBER) {
    return (
      <div className="flex flex-col gap-16">
        <Typography as="h3" variant="heading-md">
          Remove {formatRoleName(props.member.role)} access
        </Typography>

        <Typography variant="body-md" className="text-balance">
          If you remove {formatRoleName(props.member.role)} access from this
          member, they&apos;ll no longer be able to:
        </Typography>

        <ul className="mx-40 list-disc gap-16 sm:mx-auto">
          {accessOptions.map((accessOption) => (
            <li key={accessOption} className="text-left">
              <Typography variant="body-md">{accessOption}</Typography>
            </li>
          ))}
        </ul>

        <div className="flex flex-col flex-wrap justify-center gap-16 xs:flex-row">
          <Modal.Close asChild>
            <Button variant="secondary" className="w-full xs:w-fit">
              <span>Cancel</span>
            </Button>
          </Modal.Close>

          <Button
            loading={updateMemberRole.isPending}
            variant="primary"
            className="w-full xs:w-fit"
            onClick={() => {
              tracker.trackEvent(
                new ButtonClicked({
                  component_location:
                    ComponentLocations.MANAGE_CONNECT.MEMBERS_PAGE,
                  text: 'Remove access',
                  action: 'change member role to ' + props.toRole,
                })
              );

              updateMemberRole.mutate(
                {
                  memberId: props.member.id,
                  newRole: props.toRole,
                },
                { onSuccess: props.onSuccess }
              );
            }}
          >
            Remove access
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-16">
      <Typography as="h3" variant="heading-md">
        Make {formatRoleName(props.toRole)}
      </Typography>

      <Typography variant="body-md">
        If you assign {formatRoleName(props.toRole)} access to this member,
        they&apos;ll be able to:
      </Typography>

      <ul className="mx-40 list-disc gap-16 sm:mx-auto">
        {roleAccessOptions[props.toRole].map((accessOption) => (
          <li key={accessOption} className="text-left">
            <Typography variant="body-md">{accessOption}</Typography>
          </li>
        ))}
      </ul>

      {props.toRole === RoleEnum.OWNER ? (
        <Typography variant="body-md">
          You won&apos;t lose any of your user privileges by giving owner access
          to other members.
        </Typography>
      ) : (
        <Typography variant="body-md">
          You won&apos;t lose any of your user privileges by giving access to
          other members.
        </Typography>
      )}

      <div className="flex flex-col flex-wrap justify-center gap-16 xs:flex-row">
        <Modal.Close asChild>
          <Button variant="secondary" className="w-full xs:w-fit">
            <span>Cancel</span>
          </Button>
        </Modal.Close>

        <Button
          loading={updateMemberRole.isPending}
          variant="primary"
          className="w-full xs:w-fit"
          onClick={() => {
            tracker.trackEvent(
              new ButtonClicked({
                component_location:
                  ComponentLocations.MANAGE_CONNECT.MEMBERS_PAGE,
                text: 'Assign access',
                action: 'change member role to ' + props.toRole,
              })
            );

            updateMemberRole.mutate(
              {
                memberId: props.member.id,
                newRole: props.toRole,
              },

              { onSuccess: props.onSuccess }
            );
          }}
        >
          Assign access
        </Button>
      </div>
    </div>
  );
}
